/**
 * 克隆字段值 → 可读展示名 / 稳定 key
 */

function normalizeFieldOptions(options, simple) {
  if (!Array.isArray(options)) return [];
  if (options.length && typeof options[0] !== 'object') {
    return options.map((v, i) => ({
      key: String(i),
      name: String(v),
    }));
  }
  if (simple) {
    return options.map((v) => ({
      key: v.name != null ? String(v.name) : String(v.key),
      name: v.name != null ? String(v.name) : String(v.key),
    }));
  }
  return options.map((o) => ({
    key: o.key != null ? String(o.key) : String(o.name),
    name: o.name != null ? String(o.name) : String(o.key),
  }));
}

function lookupOptionLabel(raw, optionConfig) {
  optionConfig = optionConfig || {};
  const options = normalizeFieldOptions(optionConfig.options, optionConfig.simple);
  const strVal = String(raw);
  const found = options.find((o) => String(o.key) === strVal || o.name === strVal);
  return found ? found.name : null;
}

function clonedByControlDef(clonedBy) {
  if (!clonedBy || typeof clonedBy !== 'object') return null;
  return clonedBy.control || null;
}

/**
 * 列出克隆字段可配置的全部候选项（用于默认审批人配置）
 * @returns {Array<{key:string,name:string}>}
 */
export function listCloneOptionCandidates(clonedBy, clonedType, ctx) {
  ctx = ctx || {};
  const controlDef = clonedByControlDef(clonedBy);
  if (!controlDef && !clonedType) return [];

  const control = (controlDef && controlDef.control) || '';
  const optionConfig = (controlDef && controlDef.option) || {};

  const fromOptions = normalizeFieldOptions(optionConfig.options, optionConfig.simple);
  if (fromOptions.length) {
    return fromOptions;
  }

  if (clonedType === '部门' || /DepSelect/i.test(control)) {
    return (ctx.deps || []).map((d) => ({
      key: String(d.id),
      name: d.name || String(d.id),
    }));
  }

  if (clonedType === '成员' || clonedType === '员工' || /UserSelect/i.test(control)) {
    return (ctx.users || []).slice(0, 200).map((u) => ({
      key: String(u.id),
      name: u.name || String(u.id),
    }));
  }

  return [];
}

/**
 * 读取某分类对应的默认审批人
 * clonedDefaultUsers: { [keyPart]: userId | userId[] }
 */
export function resolveClonedDefaultUsers(clonedDefaultUsers, keyPart, fallbackUsers) {
  const fromMap = lookupClonedDefaultUsers(clonedDefaultUsers, keyPart);
  if (fromMap.length) return fromMap;
  return normalizeUserIds(fallbackUsers);
}

function normalizeUserIds(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((x) => {
      if (x == null) return null;
      if (typeof x === 'object') return x.id || x.user_id || x.uid || null;
      return x;
    }).filter(Boolean).map(String);
  }
  if (typeof raw === 'object') {
    const id = raw.id || raw.user_id || raw.uid;
    return id != null ? [String(id)] : [];
  }
  return [String(raw)];
}

function lookupClonedDefaultUsers(clonedDefaultUsers, keyPart) {
  if (!clonedDefaultUsers || typeof clonedDefaultUsers !== 'object') {
    return [];
  }
  if (keyPart == null || keyPart === '') return [];

  const candidates = [keyPart, String(keyPart)];
  if (typeof keyPart === 'string' && /^\d+$/.test(keyPart)) {
    candidates.push(Number(keyPart));
  }

  for (let i = 0; i < candidates.length; i++) {
    const hit = clonedDefaultUsers[candidates[i]];
    if (hit != null && hit !== '') {
      const ids = normalizeUserIds(hit);
      if (ids.length) return ids;
    }
  }

  // 宽松匹配：map 的 key 与 keyPart 转字符串后相等
  const want = String(keyPart);
  const keys = Object.keys(clonedDefaultUsers);
  for (let i = 0; i < keys.length; i++) {
    if (String(keys[i]) === want) {
      const ids = normalizeUserIds(clonedDefaultUsers[keys[i]]);
      if (ids.length) return ids;
    }
  }
  return [];
}

/**
 * @param {*} raw 表单字段中的单项值
 * @param {*} clonedBy 节点 clonedBy 配置（含 control.option）
 * @param {{ deps?: Array, users?: Array, clonedType?: string }} [ctx]
 */
export function resolveCloneItemMeta(raw, clonedBy, ctx) {
  ctx = ctx || {};
  const controlDef = clonedByControlDef(clonedBy);
  const control = (controlDef && controlDef.control) || '';
  const optionConfig = (controlDef && controlDef.option) || {};
  const clonedType = ctx.clonedType || '';

  let keyPart = raw;
  let namePart = raw;

  if (raw != null && typeof raw === 'object') {
    keyPart = raw.id != null ? raw.id :
      (raw.key != null ? raw.key : JSON.stringify(raw));
    namePart = raw.name || raw.label || raw.title || keyPart;
  }

  if (namePart === keyPart || namePart == null) {
    const idStr = String(keyPart);

    const optionLabel = lookupOptionLabel(keyPart, optionConfig);
    if (optionLabel) {
      namePart = optionLabel;
    } else if (clonedType === '部门' || /DepSelect/i.test(control)) {
      const dep = (ctx.deps || []).find((d) => String(d.id) === idStr);
      if (dep && dep.name) namePart = dep.name;
    } else if (clonedType === '成员' || clonedType === '员工' || /UserSelect/i.test(control)) {
      const user = (ctx.users || []).find((u) => String(u.id) === idStr);
      if (user && user.name) namePart = user.name;
    } else if (typeof raw === 'string' || typeof raw === 'number') {
      namePart = String(raw);
    }
  }

  return {
    keyPart: String(keyPart),
    namePart: namePart != null ? String(namePart) : String(keyPart),
    raw,
  };
}

/** 从 nodeData 的 threadId 反查可读标签 */
export function resolveCloneThreadLabel(threadId, nodeId, formData, nodeOption, ctx) {
  nodeOption = nodeOption || {};
  ctx = ctx || {};
  if (!threadId || !nodeId) return threadId || '';
  const suffix = threadId.startsWith(nodeId + '_') ?
    threadId.slice(nodeId.length + 1) :
    threadId;

  const clonedBy = nodeOption.clonedBy;
  if (!clonedBy || !formData) return suffix;

  const key = typeof clonedBy === 'string' ? clonedBy : (clonedBy.key || '');
  if (!key) return suffix;

  const paths = key.split('.').filter(Boolean);
  let value = formData;
  for (let i = 0; i < paths.length; i++) {
    if (value == null) return suffix;
    value = value[paths[i]];
  }
  const list = Array.isArray(value) ? value : (value != null && value !== '' ? [value] : []);
  const resolveCtx = Object.assign({clonedType: nodeOption.clonedType}, ctx);
  const hit = list.find((item) => {
    const meta = resolveCloneItemMeta(item, clonedBy, resolveCtx);
    return meta.keyPart === String(suffix);
  });
  if (hit != null) {
    return resolveCloneItemMeta(hit, clonedBy, resolveCtx).namePart;
  }
  return suffix;
}
